<template>
  <div id="lower-thirds-output" ref="lowerThirdsOutput">
    <QueueSyncStatus v-if="$route.query.monitor === '1'" :state="syncState" />
    <div
      v-if="currentItem?.type === 'student' && primaryValue"
      class="lower-third student"
      :class="{ 'has-logo': logoUrl }"
      :style="studentBoxStyle"
    >
      <div v-if="logoUrl" class="student-logo-frame" :style="logoStyle">
        <img class="student-logo" :src="logoUrl" alt="Event logo" />
      </div>
      <div ref="studentCopy" class="student-copy">
        <div ref="primaryLine" class="primary-line" :style="studentPrimaryStyle">{{ primaryValue }}</div>
        <div
          ref="secondaryLine"
          v-if="showSecondaryLine && secondaryValue"
          class="secondary-line"
          :style="studentSecondaryStyle"
        >
          {{ secondaryValue }}
        </div>
      </div>
      <div v-if="logoUrl" class="student-spacer"></div>
    </div>

    <div v-else-if="currentItem?.type === 'display_line' && headlineValue" class="lower-third display-line" :style="displayBoxStyle">
      <div ref="headlineOnly" class="headline-only" :style="displayHeadlineStyle">{{ headlineValue }}</div>
    </div>
  </div>
</template>

<script>
import { nextTick } from "vue";
import queueSyncMixin from "@/lib/queueSyncMixin";
import {
  cloneViews,
  DEFAULT_VIEWS,
  lowerThirdsBrandingStyle,
  lowerThirdsLogoStyle,
  resolveLowerThirdsLogoUrl,
  resolveDisplayValue,
} from "@/lib/viewConfig";

export default {
  name: "LowerThirdsOutput",
  mixins: [queueSyncMixin],
  data() {
    return {
      snapshot: null,
      viewConfig: cloneViews(DEFAULT_VIEWS),
      lastQueueRevision: 0,
      fitFrame: null,
      frameWidthPx: 1920,
      frameHeightPx: 1080,
      fittedPrimaryFontSize: null,
      fittedSecondaryFontSize: null,
      fittedHeadlineFontSize: null,
    };
  },
  watch: {
    currentItem: {
      deep: true,
      handler() {
        this.scheduleAutoFit();
      },
    },
    viewConfig: {
      deep: true,
      handler() {
        this.scheduleAutoFit();
      },
    },
  },
  computed: {
    eventId() {
      return this.$route.params.eventId;
    },
    currentItem() {
      const items = this.snapshot?.queue?.items || [];
      const visibleIndex = this.snapshot?.queue?.visibleIndex;
      if (visibleIndex === null || visibleIndex === undefined) {
        return null;
      }
      const item = items[visibleIndex] || null;
      if (item?.type === "presenter_note") {
        return null;
      }
      return this.withParticipantExtraFields(item);
    },
    studentConfig() {
      return this.viewConfig.lowerThirds.student;
    },
    displayConfig() {
      return this.viewConfig.lowerThirds.displayLine;
    },
    logoUrl() {
      return resolveLowerThirdsLogoUrl(this.viewConfig.lowerThirds.branding);
    },
    brandingStyle() {
      return lowerThirdsBrandingStyle(this.viewConfig.lowerThirds.branding);
    },
    logoStyle() {
      return lowerThirdsLogoStyle(this.viewConfig.lowerThirds.branding);
    },
    primaryValue() {
      if (!this.currentItem || this.currentItem.type !== "student") {
        return "";
      }
      return resolveDisplayValue(this.currentItem, this.studentConfig.primaryField);
    },
    secondaryValue() {
      if (!this.currentItem || this.currentItem.type !== "student") {
        return "";
      }
      return resolveDisplayValue(this.currentItem, this.studentConfig.secondaryField);
    },
    showSecondaryLine() {
      return this.studentConfig.showSecondaryLine;
    },
    headlineValue() {
      if (!this.currentItem || this.currentItem.type !== "display_line") {
        return "";
      }
      return resolveDisplayValue(this.currentItem, this.displayConfig.headlineField);
    },
    studentBoxStyle() {
      return {
        ...this.brandingStyle,
        textAlign: this.studentConfig.alignment,
      };
    },
    studentPrimaryStyle() {
      return {
        fontSize: this.fittedPrimaryFontSize || this.fontSizeFor("primary", this.studentConfig.fontScale),
        textAlign: this.studentConfig.alignment,
      };
    },
    studentSecondaryStyle() {
      return {
        fontSize: this.fittedSecondaryFontSize || this.fontSizeFor("secondary", this.studentConfig.fontScale),
        textAlign: this.studentConfig.alignment,
      };
    },
    displayBoxStyle() {
      return {
        ...this.brandingStyle,
        alignItems: this.displayConfig.alignment === "center" ? "center" : "flex-start",
      };
    },
    displayHeadlineStyle() {
      return {
        fontSize: this.fittedHeadlineFontSize || this.fontSizeFor("headline", this.displayConfig.fontScale),
        textAlign: this.displayConfig.alignment,
      };
    },
  },
  methods: {
    withParticipantExtraFields(item) {
      if (!item || item.extraFields) {
        return item;
      }
      const participantId = item.sourceParticipantId || item.id || "";
      if (!participantId) {
        return item;
      }
      const participant = (this.snapshot?.participants?.items || []).find(
        (candidate) => candidate.id === participantId
      );
      const extraFields = participant?.extraFields || {};
      if (!Object.keys(extraFields).length) {
        return item;
      }
      return {
        ...item,
        extraFields,
      };
    },
    framePixels() {
      return this.frameHeightPx || this.$refs.lowerThirdsOutput?.clientHeight || 1080;
    },
    barPixels() {
      return this.framePixels() * 0.2;
    },
    fontSizeFor(kind, scale) {
      const barHeight = this.barPixels();
      const ratios = {
        primary: { sm: 0.22, md: 0.27, lg: 0.32, xl: 0.36 },
        secondary: { sm: 0.11, md: 0.13, lg: 0.16, xl: 0.18 },
        headline: { sm: 0.18, md: 0.22, lg: 0.27, xl: 0.31 },
      };
      const value = ratios[kind]?.[scale] || ratios[kind]?.lg || 0.04;
      return `${Math.round(barHeight * value)}px`;
    },
    minFontPixels(kind) {
      const barHeight = this.barPixels();
      const ratios = {
        primary: 0.16,
        secondary: 0.08,
        headline: 0.13,
      };
      return Math.round(barHeight * (ratios[kind] || 0.1));
    },
    resetAutoFit() {
      this.fittedPrimaryFontSize = null;
      this.fittedSecondaryFontSize = null;
      this.fittedHeadlineFontSize = null;
    },
    scheduleAutoFit() {
      this.resetAutoFit();
      if (this.fitFrame) {
        window.cancelAnimationFrame(this.fitFrame);
      }
      this.fitFrame = window.requestAnimationFrame(async () => {
        this.fitFrame = null;
        await nextTick();
        this.autoFitText();
      });
    },
    autoFitText() {
      if (this.currentItem?.type === "student") {
        this.fitStudentText();
        return;
      }
      if (this.currentItem?.type === "display_line") {
        this.fitDisplayLineText();
      }
    },
    fitStudentText() {
      const primaryEl = this.$refs.primaryLine;
      const secondaryEl = this.$refs.secondaryLine;
      if (!primaryEl) {
        return;
      }

      this.fittedPrimaryFontSize = this.fitTextElement(
        primaryEl,
        this.fontSizeFor("primary", this.studentConfig.fontScale),
        this.minFontPixels("primary")
      );

      if (secondaryEl && this.showSecondaryLine && this.secondaryValue) {
        this.fittedSecondaryFontSize = this.fitTextElement(
          secondaryEl,
          this.fontSizeFor("secondary", this.studentConfig.fontScale),
          this.minFontPixels("secondary")
        );
      }
    },
    fitDisplayLineText() {
      const headlineEl = this.$refs.headlineOnly;
      if (!headlineEl) {
        return;
      }

      this.fittedHeadlineFontSize = this.fitTextElement(
        headlineEl,
        this.fontSizeFor("headline", this.displayConfig.fontScale),
        this.minFontPixels("headline")
      );
    },
    fitTextElement(element, baseSize, minSizePx) {
      const maxSizePx = this.toPixels(baseSize);
      let currentSize = maxSizePx;
      const containerWidth = element.parentElement?.clientWidth || element.clientWidth || 0;
      element.style.fontSize = `${currentSize}px`;

      while (containerWidth > 0 && element.scrollWidth > containerWidth + 1 && currentSize > minSizePx) {
        currentSize -= 2;
        element.style.fontSize = `${currentSize}px`;
      }

      return `${currentSize}px`;
    },
    toPixels(value) {
      if (typeof value === "number") {
        return value;
      }

      const normalized = `${value || ""}`.trim();
      if (!normalized) {
        return 16;
      }
      if (normalized.endsWith("px")) {
        return Number.parseFloat(normalized);
      }
      if (normalized.endsWith("rem")) {
        const rootFontSize = Number.parseFloat(
          window.getComputedStyle(document.documentElement).fontSize || "16"
        );
        return Number.parseFloat(normalized) * rootFontSize;
      }
      return Number.parseFloat(normalized) || 16;
    },
    queueRevisionFor(queue) {
      const revision = Number.parseInt(queue?.revision || 0, 10);
      return Number.isNaN(revision) ? 0 : revision;
    },
    applySnapshot(snapshot, { includeViews = false } = {}) {
      const incomingQueueRevision = this.queueRevisionFor(snapshot.queue);
      const currentQueueRevision = Math.max(
        this.lastQueueRevision,
        this.queueRevisionFor(this.snapshot?.queue)
      );
      const shouldKeepCurrentQueue =
        this.snapshot?.queue && incomingQueueRevision < currentQueueRevision;
      const nextQueue = shouldKeepCurrentQueue ? this.snapshot.queue : snapshot.queue;

      if (!this.snapshot) {
        this.snapshot = {
          ...snapshot,
          queue: nextQueue,
        };
      } else {
        this.snapshot = {
          ...this.snapshot,
          event: snapshot.event,
          participants: snapshot.participants,
          queue: nextQueue,
          workflow: snapshot.workflow,
        };
      }

      if (includeViews) {
        this.viewConfig = cloneViews(snapshot.views || DEFAULT_VIEWS, snapshot.event);
      }
      if (!shouldKeepCurrentQueue) {
        this.lastQueueRevision = incomingQueueRevision;
      }
    },

    setAspectRatio() {
      const aspectRatio = 16 / 9;
      let width = window.innerWidth;
      let height = window.innerHeight;

      if (width / height > aspectRatio) {
        width = height * aspectRatio;
      } else {
        height = width / aspectRatio;
      }

      const lowerThirdsOutput = this.$refs.lowerThirdsOutput;
      if (lowerThirdsOutput) {
        this.frameWidthPx = width;
        this.frameHeightPx = height;
        lowerThirdsOutput.style.width = `${width}px`;
        lowerThirdsOutput.style.height = `${height}px`;
        lowerThirdsOutput.style.left = `${(window.innerWidth - width) / 2}px`;
        lowerThirdsOutput.style.top = `${(window.innerHeight - height) / 2}px`;
      }
      this.scheduleAutoFit();
    },
  },
  async mounted() {
    this.startQueueSync();
    window.addEventListener("resize", this.setAspectRatio);
    this.setAspectRatio();
  },
  beforeUnmount() {
    if (this.fitFrame) {
      window.cancelAnimationFrame(this.fitFrame);
    }
    window.removeEventListener("resize", this.setAspectRatio);
  },
};
</script>

<style scoped>
#lower-thirds-output {
  position: absolute;
  background-color: #00fd19;
  transform-origin: top left;
  font-family: Arial, sans-serif;
}

.lower-third {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 20%;
  padding-inline: 2.5%;
  padding-block: 0.2em;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr;
  align-items: stretch;
  overflow: hidden;
}

.lower-third.student.has-logo {
  grid-template-columns: 20% 1fr 20%;
}

.student {
  background: #2f2552;
  color: white;
}

.display-line {
  background: #2f2552;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.primary-line {
  font-weight: 400;
  line-height: 1.1;
  margin: 0;
  padding-top: 0;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
}

.secondary-line {
  margin: 0.18em 0 0 0;
  opacity: 0.95;
  line-height: 1.2;
  padding-bottom: 0;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
}

.headline-only {
  font-weight: 700;
  width: 100%;
  margin: 0;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
}

.student-logo-frame {
  width: 100%;
  height: 72%;
  max-height: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: start;
  align-self: center;
  overflow: hidden;
}

.student-logo {
  width: auto;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  object-position: center center;
}

.student-copy {
  grid-column: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: inherit;
  min-width: 0;
  width: 100%;
  overflow: hidden;
  align-self: stretch;
}

.student.has-logo .student-copy {
  grid-column: 2;
}

.student-spacer {
  width: 100%;
  height: 1px;
}
</style>
